/**
 * Canonical scan metadata extracted from raw scanner text modules.
 * Used by both parsePccFile() and PCCUtils.decode() / parseRawFiles().
 */
export interface ScanMetadata {
  manufacturer: string;
  model: string;
  osName: string;
  totalRamGb: number;
  diskTotalGb: number;
  diskFreeGb: number;
  ramSlotsUsed: number;
  systemType: string;
}

const UNKNOWN_MANUFACTURER = 'Unknown Manufacturer';
const UNKNOWN_MODEL = 'Unknown Model';

/** Decode text from scanner output (UTF-8, UTF-16 LE/BE, or BOM-marked). */
export function decodeDiagnosticText(buffer: Uint8Array | ArrayBuffer): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  if (bytes.length === 0) return '';

  // Check for UTF-16 BOMs
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes);
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes);
  }
  // Check for UTF-8 BOM
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3));
  }

  // Heuristic: systeminfo.exe via CMD redirect is often UTF-16 LE without BOM
  if (bytes.length > 4 && bytes[1] === 0 && bytes[3] === 0) {
    return new TextDecoder('utf-16le').decode(bytes);
  }

  return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** 
 * Extracts a field value by trying multiple localized keys.
 * Essential for stability on non-English Windows (DE, FR, ES, etc).
 */
function extractLocalizedField(text: string, keys: string[]): string {
  for (const key of keys) {
    const escaped = escapeRegex(key);
    const patterns = [
      new RegExp(`^${escaped}\\s*:\\s*(.+)$`, 'im'),
      new RegExp(`${escaped}\\s*:\\s*(.+)`, 'i'),
    ];
    for (const re of patterns) {
      const match = text.match(re);
      if (match?.[1]?.trim()) return match[1].trim();
    }
  }
  return '';
}

/** Handles localized numbers with commas as decimal separators (e.g. "16.384 MB" or "16,384 MB"). */
function parseLocalizedNumber(value: string): number {
  if (!value) return 0;
  // Remove thousand separators (dots or commas followed by 3 digits)
  // e.g. 1.234.567 or 1,234,567 -> 1234567
  const cleaned = value.replace(/(\d)[.,](\d{3})/g, '$1$2').replace(/[^\d.,]/g, '');
  
  // If there's a remaining dot/comma, it's likely a decimal separator
  const final = cleaned.replace(',', '.');
  const num = parseFloat(final);
  return isNaN(num) ? 0 : num;
}

function parseRamMb(value: string): number {
  if (!value) return 0;
  const match = value.match(/([\d.,\s]+)\s*MB/i);
  if (match) return parseLocalizedNumber(match[1]);
  return parseLocalizedNumber(value);
}

function parseDiskBytes(disk: string, key: string): number {
  const escaped = escapeRegex(key);
  const match = disk.match(new RegExp(`${escaped}\\s*:\\s*(\\d+)`, 'i'));
  return match ? parseInt(match[1], 10) : 0;
}

function countRamSlots(ram: string): number {
  if (!ram) return 0;
  const capacities = [...ram.matchAll(/Capacity\s*:\s*(\d+)/gi)];
  return capacities.length;
}

function sanitizeLabel(value: string, fallback: string): string {
  const trimmed = value.trim();
  if (!trimmed || /^unknown$/i.test(trimmed)) return fallback;
  // Common placeholders
  const placeholders = [
    /to be filled by o\.?e\.?m\.?/i,
    /default string/i,
    /not applicable/i,
    /system product name/i
  ];
  if (placeholders.some(re => re.test(trimmed))) return fallback;
  return trimmed;
}

export function extractScanMetadata(
  systeminfo: string,
  disk = '',
  ram = ''
): ScanMetadata {
  // Localization Map for critical fields
  const manufacturerKeys = ['System Manufacturer', 'Systemhersteller', 'Fabricant du système', 'Fabricante del sistema', 'Produttore sistema'];
  const modelKeys = ['System Model', 'Systemmodell', 'Modèle', 'Modelo del sistema', 'Modello sistema'];
  const osKeys = ['OS Name', 'Betriebsystemname', 'Nom de l\'OS', 'Nombre del sistema operativo', 'Nome SO', 'OS Caption'];
  const ramKeys = ['Total Physical Memory', 'Gesamter physikalischer Speicher', 'Mémoire physique totale', 'Memoria física total', 'Memoria fisica totale'];
  const systemTypeKeys = ['System Type', 'Systemtyp', 'Type du système', 'Tipo de sistema', 'Tipo sistema'];

  const manufacturerRaw = extractLocalizedField(systeminfo, manufacturerKeys);
  const modelRaw = extractLocalizedField(systeminfo, modelKeys);
  const osNameRaw = extractLocalizedField(systeminfo, osKeys);
  const systemTypeRaw = extractLocalizedField(systeminfo, systemTypeKeys);

  const totalRamStr = extractLocalizedField(systeminfo, ramKeys);
  const totalRamMb = parseRamMb(totalRamStr);
  const totalRamGb = totalRamMb > 0 ? Math.round((totalRamMb / 1024) * 10) / 10 : 0;

  const diskSizeBytes = parseDiskBytes(disk, 'Size');
  const diskFreeBytes = parseDiskBytes(disk, 'FreeSpace');
  const diskTotalGb =
    diskSizeBytes > 0 ? Math.round((diskSizeBytes / 1024 ** 3) * 10) / 10 : 0;
  const diskFreeGb =
    diskFreeBytes > 0 ? Math.round((diskFreeBytes / 1024 ** 3) * 10) / 10 : 0;

  const ramSlotsUsed = countRamSlots(ram);

  return {
    manufacturer: sanitizeLabel(manufacturerRaw, UNKNOWN_MANUFACTURER),
    model: sanitizeLabel(modelRaw, UNKNOWN_MODEL),
    osName: sanitizeLabel(osNameRaw, 'Windows'),
    totalRamGb,
    diskTotalGb,
    diskFreeGb,
    ramSlotsUsed,
    systemType: sanitizeLabel(systemTypeRaw, 'x64-based PC'),
  };
}

/** Apply canonical metadata onto a PCC-shaped object (system + storage + ram). */
export function applyScanMetadata(target: Record<string, unknown>, meta: ScanMetadata): void {
  if (!target.system || typeof target.system !== 'object') {
    target.system = {};
  }
  const system = target.system as Record<string, unknown>;

  if (meta.manufacturer && meta.manufacturer !== UNKNOWN_MANUFACTURER) {
    system.manufacturer = meta.manufacturer;
  }
  if (meta.model && meta.model !== UNKNOWN_MODEL) {
    system.model = meta.model;
  }
  if (meta.osName && meta.osName !== 'Windows') {
    system.os = meta.osName;
  } else if (!system.os) {
    system.os = meta.osName;
  }
  if (meta.totalRamGb > 0) {
    system.ramGB = meta.totalRamGb;
  }
  if (meta.systemType) {
    system.systemType = meta.systemType;
  }

  if (!target.storage || typeof target.storage !== 'object') {
    target.storage = { mainDrive: {} };
  }
  const storage = target.storage as Record<string, unknown>;
  if (!storage.mainDrive || typeof storage.mainDrive !== 'object') {
    storage.mainDrive = {};
  }
  const mainDrive = storage.mainDrive as Record<string, unknown>;
  if (meta.diskTotalGb > 0) {
    mainDrive.sizeGB = meta.diskTotalGb;
  }
  if (meta.diskFreeGb > 0) {
    mainDrive.freeGB = meta.diskFreeGb;
  }
  if (!mainDrive.model) {
    mainDrive.model = 'System Drive';
  }
  if (!mainDrive.status) {
    mainDrive.status =
      meta.diskTotalGb > 0 && meta.diskFreeGb < 20 ? 'Near Capacity' : 'Healthy';
  }

  if (!target.ram || typeof target.ram !== 'object') {
    target.ram = {};
  }
  const ram = target.ram as Record<string, unknown>;
  if (meta.totalRamGb > 0) {
    ram.totalGB = meta.totalRamGb;
  }
  if (meta.ramSlotsUsed > 0) {
    ram.slotsUsed = meta.ramSlotsUsed;
  }
}
